import * as Yup from "yup";
import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import MoreVert from "../../assets/icons/svg/moreVert.svg?react";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";
import { useClickOutside } from "../../utils";
import { Tooltip } from "..";
import { connect } from "react-redux";
import { updateModal } from "@/store/app/app.slice";
import { ConnectedProps } from "react-redux";
import { logoutUser } from "@/store/user/user.action";
import { RootState } from "../../store";
import { CircleX } from "lucide-react";
export interface UserSettingsProps {
  productOptions?: any;
  businessOptions?: any;
  metaLanguageOptions?: any;
  onSettingsChange?: (values: any) => void;
  currentUser?: any;
  updateModal: (isOpen: boolean) => void;
}

interface FormValues {
  notifications: {
    upload_success: boolean;
    upload_fail: boolean;
    youtube_published: boolean;
  };
  settings: {
    landing_page: string;
    language: string;
    meta_language: string;
  };
  default_business: string;
  default_team: string;
}

interface Session {
  is_current: boolean;
  id: string;
  host: string;
  ct: number;
  mt: number;
}

const UserSettings = ({
  onSettingsChange,
  currentUser,

  updateModal,
  logoutUser,
}: UserSettingsProps & ConnectedProps<typeof connector>) => {
  const [visibleSessions, setVisibleSessions] = useState(20); // Changed from 10 to 4
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [logoutAction, setLogoutAction] = useState<
    "other" | "everywhere" | null
  >(null);
  const [sessions, setSessions] = useState<Session[]>(
    currentUser?.session || []
  );
  const moreOptionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSessions(currentUser?.session || []);
  }, [currentUser?.session]);

  useEffect(() => {
    if (sessions.length <= 21) {
      setVisibleSessions(sessions.length);
    } else {
      setVisibleSessions(20);
    }
  }, [sessions.length]);

  const handleMoreOptionsToggle = () => {
    setShowMoreOptions((prevState) => !prevState);
  };

  const handleCloseModal = () => {
    setConfirmModalOpen(false);
    updateModal(false);
    setLogoutAction(null);
  };

  const handleOpenModal = () => {
    setConfirmModalOpen(true);
    updateModal(true);
  };

  const handleLogoutClick = (action: "other" | "everywhere") => {
    setLogoutAction(action);
    handleOpenModal();
    setShowMoreOptions(false);
  };

  const handleConfirmLogout = async () => {
    try {
      if (logoutAction === "other") {
        await logoutUser({ type: "all" });
        setSessions((prevSessions: Session[]) =>
          prevSessions.filter((session: Session) => !session.is_current)
        );
      } else if (logoutAction === "everywhere") {
        await logoutUser({ type: "everywhere" });
        setSessions((prevSessions: Session[]) =>
          prevSessions.filter((session: Session) => !session.is_current)
        );
      }
      handleCloseModal();
    } catch (error) {
      console.error("Logout failed:", error);
      handleCloseModal();
    }
  };

  const handleIndividualLogout = async (
    sessionId: string,
    e?: any,
    el?: any
  ) => {
    try {
      await logoutUser({ type: "individual", id: sessionId });
      setSessions((prevSessions: Session[]) =>
        prevSessions.filter((session: Session) => session.id !== sessionId)
      );
    } catch (error) {
      console.error("Individual logout failed:", error);
    }
  };

  useClickOutside(moreOptionsRef, () => {
    if (showMoreOptions) {
      handleMoreOptionsToggle();
    }
  });

  const handleLoadMore = () => {
    setVisibleSessions(sessions.length);
  };
  const initialValues: FormValues = {
    notifications: {
      upload_success: currentUser?.notifications?.upload_success,
      upload_fail: currentUser?.notifications?.upload_fail,
      youtube_published: currentUser?.notifications?.youtube_published,
    },
    settings: {
      landing_page: currentUser?.settings?.landing_page,
      language: currentUser?.settings?.language,
      meta_language: currentUser?.settings?.meta_language,
    },
    default_business: currentUser?.default_business || "",
    default_team: currentUser?.default_team || "",
  };

  const validationSchema = Yup.object({
    language: Yup.string().required("Content Language is required"),
    meta_language: Yup.string().required("Meta-Data Language is required"),
    default_business: Yup.string().required("Business is required"),
    default_team: Yup.string().required("Team is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSettingsChange?.(values);
    },
  });

  return (
    <>
      {/* Session Table */}
      <div className="card p-0">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h6>Session</h6>
          <div className="dropdown">
            <MoreVert
              onClick={handleMoreOptionsToggle}
              className="dropdown-toggle iconSvg"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            />
            {showMoreOptions && (
              <div
                ref={moreOptionsRef}
                className=""
                style={{
                  zIndex: 1000,
                }}
              >
                <ul
                  className="dropdown-menu"
                  style={{ listStyle: "none", display: "block", right: "0" }}
                >
                  <li
                    className="text-capitalize"
                    onClick={() => handleLogoutClick("other")}
                  >
                    <a className="dropdown-item d-flex align-items-center">
                      Logout other sessions
                    </a>
                  </li>
                  <li
                    className="text-capitalize"
                    onClick={() => handleLogoutClick("everywhere")}
                  >
                    <a className="dropdown-item d-flex align-items-center">
                      Logout Everywhere
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <table className="table mb-0">
          <thead>
            <tr>
              <th className="p-3 py-0 bg-light fw-bold sticky-th-top">ID</th>
              <th className="p-3 py-0 bg-light fw-bold sticky-th-top">Host</th>
              <th className="p-3 py-0 bg-light fw-bold sticky-th-top">
                Created
              </th>
              <th className="p-3 py-0 bg-light fw-bold sticky-th-top">
                Last Active
              </th>
              <th
                className="p-3 py-0 bg-light fw-bold sticky-th-top align-center"
                style={{ textAlign: "center" }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {sessions
              ? [...sessions]
                  .sort((a: any, b: any) => b?.mt - a?.mt)
                  .slice(0, visibleSessions)
                  .map((session: any) => (
                    <tr
                      key={session.id}
                      className={session.is_current ? "active" : ""}
                    >
                      <td className="px-3 py-2 align-middle">
                        <b>{session.id}</b>
                        <br />
                        <small className="pt-1">
                          <b>IP:</b>
                          {session?.ip} | <b>UA:</b> {session?.ua}
                        </small>
                      </td>
                      <td className="px-3 py-2 align-middle">{session?.ip}</td>
                      <td className="px-3 py-2 align-middle">
                        {new Date(session.ct).toLocaleString([], {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        <br />
                        <small>
                          <b>IP:</b> {session?.ip}
                        </small>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        {new Date(session.mt).toLocaleString([], {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        <br />
                        <small>
                          <b>IP: {session?.ip}</b>
                        </small>
                      </td>
                      <td className="px-3 py-2 align-middle" align="center">
                        {!session.is_current && (
                          <Tooltip content="Logout">
                            <CircleX
                              size={28}
                              strokeWidth={1}
                              className="svg-btn logout"
                              onClick={(e) =>
                                handleIndividualLogout(session.id, e)
                              }
                            />
                          </Tooltip>
                        )}
                        {session.is_current && (
                          <span className="badge text-bg-primary f-11">
                            current
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
              : null}
          </tbody>
        </table>
      </div>
      {sessions.length > 8 && visibleSessions < sessions.length && (
        <div className="d-flex justify-content-center align-items-center mt-2">
          <div
            className="loadMore d-flex align-items-center z-1"
            onClick={handleLoadMore}
            style={{ cursor: "pointer" }}
          >
            Load More
          </div>
        </div>
      )}
      <AlertDialog
        isOpen={isConfirmModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmLogout}
        title="ALERT!"
        message={
          logoutAction === "other"
            ? "Are you sure you want to logout from all other sessions?"
            : "Are you sure you want to logout from all sessions everywhere?"
        }
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </>
  );
};

const mapState = (state: RootState) => ({});

const mapDispatch = {
  updateModal,
  logoutUser,
};

const connector = connect(mapState, mapDispatch);

export default connector(UserSettings);
